import { lazy, type ComponentType, type LazyExoticComponent } from "react";

// One flag per chunk. A single shared flag would let a successful load of any
// chunk clear the guard for a chunk that is genuinely missing, so every attempt
// to open the broken one would reload the page again.
const RELOAD_FLAG_PREFIX = "tower.chunkReloaded.";

function flagKey(chunkName: string): string {
  return `${RELOAD_FLAG_PREFIX}${chunkName}`;
}

function hasAlreadyReloaded(chunkName: string): boolean {
  try {
    return sessionStorage.getItem(flagKey(chunkName)) !== null;
  } catch {
    // Storage can be unavailable (private mode, blocked storage). Treat that as
    // "already reloaded" so a broken chunk can never cause a reload loop.
    return true;
  }
}

function markReloaded(chunkName: string): void {
  try {
    sessionStorage.setItem(flagKey(chunkName), "1");
  } catch {
    // hasAlreadyReloaded() already suppresses the reload when storage fails.
  }
}

function clearReloadFlag(chunkName: string): void {
  try {
    sessionStorage.removeItem(flagKey(chunkName));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/**
 * React.lazy for chunks that can disappear underneath a running tab. Every
 * deploy replaces the hashed files in /assets, and nginx serves those with
 * `try_files $uri =404`, so a tab still holding the previous index.html gets a
 * 404 for the chunk it asks for. Retry once to absorb a flaky network, then
 * reload to pick up a fresh index.html pointing at the new hashes.
 *
 * `chunkName` scopes the once-only reload guard, so a chunk that stays broken
 * costs at most one reload per session while a later mismatch in a different
 * chunk still gets its own chance to recover.
 *
 * Routes do not need this — `lazyRouteComponent` from TanStack Router has its
 * own reload-on-missing-module handling.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  chunkName: string,
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const loaded = await importer();
      // A successful load means we are running against current assets again.
      clearReloadFlag(chunkName);
      return loaded;
    } catch (error) {
      try {
        return await importer();
      } catch {
        if (!hasAlreadyReloaded(chunkName)) {
          markReloaded(chunkName);
          window.location.reload();
        }
        throw error;
      }
    }
  });
}
